/** @file Allocates one rig's viewport scene: pose, scratch samples, skeleton, overlay/gizmo chunks, ghosts. */
import { createSample } from '@pose2d/core/anim/createSample';
import { createPose } from '@pose2d/core/pose/createPose';
import type { Pose, Sample, SkeletonData } from '@pose2d/core/types';
import { createRenderBuffers } from '@pose2d/geometry/createRenderBuffers';
import type { RenderBuffers } from '@pose2d/geometry/types';

/** Allocated once per compiled rig, rewritten in place every dirty frame. */
export interface Scene {
  data: SkeletonData;
  pose: Pose;
  /** Three distinct samples, poseAt's scratch (applyState needs three; one animation uses the first). */
  scratch: Sample[];
  /** Skeleton triangles, always built with `splitRuns: 'slot'` (pickSlot reads the runs). */
  skeleton: RenderBuffers;
  overlay: RenderBuffers[];
  gizmo: RenderBuffers[];
  ghosts: Pose[];
}

/** A new rig gets new buffers (capacities come from SkeletonData). */
export function createScene(data: SkeletonData): Scene {
  return {
    data, pose: createPose(data), scratch: [createSample(data), createSample(data), createSample(data)],
    skeleton: createRenderBuffers(data), overlay: [], gizmo: [], ghosts: [],
  };
}
